import type { QueryKey } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { CollapseState } from '@/features/AskSyncly/types/constants';
import type { FeedbackBlockListModalOptions } from '@/features/Feedback/components/FeedbackListModal/types';
import type { WordCloudBasis } from '@/features/WordCloud/types';
import service from '@/services';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import type { FeedbackListItemWithBlock } from '@/types/feedback';
import { GPT_DOMAIN } from '@/types/gptConst';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

type SignalFeedbackBlockListModalParams = {
  title: string;
  description?: ReactNode;
  queryKey: QueryKey;
  signalPayload?: StructuredFilter;
  initialOpen?: boolean;
  fetchFeedbacks: (
    sortStatus: NotNullableSortStatusT,
    pageParam?: null | string,
  ) => Promise<{
    count: number;
    list: FeedbackListItemWithBlock[];
    paginate_token?: null | string;
  }>;
};

function openSignalFeedbackBlockListModal({
  title,
  description,
  queryKey,
  signalPayload,
  initialOpen,
  fetchFeedbacks,
}: SignalFeedbackBlockListModalParams) {
  const alias = JSON.stringify(queryKey);

  useDetailModalStore.getState().setNewModal<FeedbackBlockListModalOptions>({
    alias,
    type: DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST,
    idx: 0,
    keys: [alias],
    options: {
      queryKeys: [alias],
      title,
      subTitle: description,
      domain: 'feedback-blocks',
      fetcher: fetchFeedbacks,
      socketInfo: signalPayload
        ? {
            domain: GPT_DOMAIN.SIGNAL,
            signal: signalPayload,
            initialState: initialOpen ? CollapseState.EXPANDED : undefined,
          }
        : undefined,
    },
  });
}

type PreviewFeedbackListByFilterParams = {
  title: string;
  queryKey: QueryKey;
  payload: StructuredFilter;
  description?: ReactNode;
  initialOpen?: boolean;
  onExitComplete?: () => void;
};

export function openSignalFilterFeedbackPreviewDialog({
  title,
  queryKey,
  payload,
  description,
  initialOpen,
}: PreviewFeedbackListByFilterParams) {
  return openSignalFeedbackBlockListModal({
    title,
    description,
    queryKey,
    signalPayload: payload,
    initialOpen,
    fetchFeedbacks: async (sortStatus, pageParam) => {
      const response = await service.feedback.fetchFeedbackBlockList({
        query: {
          sortby: sortStatus.sortBy,
          order: sortStatus.orderStatus,
          direction: 'next',
          cursor: pageParam || undefined,
        },
        payload,
      });
      const feedbacks = response.feedbacks ?? [];

      return {
        count: response.count,
        list: feedbacks,
        paginate_token:
          feedbacks[feedbacks.length - 1]?.paginate_token ?? null,
      };
    },
  });
}

type PreviewFeedbackListByKeywordParams = {
  title: string;
  queryKey: QueryKey;
  filter?: StructuredFilter;
  keyword: string;
  basis?: WordCloudBasis;
  description?: ReactNode;
  onExitComplete?: () => void;
};

export function openSignalWordCloudPreviewDialog({
  title,
  queryKey,
  filter,
  keyword,
  basis,
  description,
}: PreviewFeedbackListByKeywordParams) {
  return openSignalFeedbackBlockListModal({
    title,
    description,
    queryKey,
    fetchFeedbacks: async (sortStatus, pageParam) => {
      const response = await service.wordCloud.fetchWordCloudFeedback(
        getCurrWorkspaceAlias(),
        {
          sortBy: sortStatus.sortBy,
          orderStatus: sortStatus.orderStatus,
        },
        {
          filter,
          keyword,
          basis,
          paginate_token: pageParam || undefined,
        },
      );

      return {
        count: response.count,
        list: response.feedbacks,
        paginate_token:
          response.feedbacks[response.feedbacks.length - 1]?.paginate_token ??
          null,
      };
    },
  });
}

type PreviewFeedbackListByReferencesParams = {
  title: string;
  queryKey: QueryKey;
  references: {
    feedback_id: string;
    block_id: string[];
  }[];
  description?: ReactNode;
  onExitComplete?: () => void;
};

export function openSignalReferencesPreviewDialog({
  title,
  queryKey,
  references,
  description,
}: PreviewFeedbackListByReferencesParams) {
  return openSignalFeedbackBlockListModal({
    title,
    description,
    queryKey,
    fetchFeedbacks: async () => {
      const list =
        await service.feedback.fetchFeedbackBlockListByReferences(references);

      return {
        count: list.length,
        list,
        paginate_token: null,
      };
    },
  });
}
